import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { lazyWithPreload } from "utils/lazyWithPreload";
import { ROUTES } from "utils/routes.constants";

import { useCreditsFlag } from "featureFlags/hooks/useCreditsFlag";
import { useEnableOrbac } from "featureFlags/hooks/useEnableOrbac";
import { useFormatPath } from "hooks/useFormatPath";
import { usePreloadComponents } from "hooks/usePreloadedComponents";
import { useAccounts } from "services/account/useAccounts";
import { useHasAccountAdminRole } from "services/auth/useHasAccountAdminRole";
import { useHasAdminRole } from "services/auth/useHasAdminRole";

import SqlIcon from "assets/icons/mainMenuIcons/Develop.svg?react";
import GovernIcon from "assets/icons/mainMenuIcons/Govern.svg?react";
import ManagementIcon from "assets/icons/mainMenuIcons/Management.svg?react";

import { accessManager } from "components/App/accessManager";
import { LogoIcon } from "components/Icons";
import MenuItem from "components/MainSidebar/MenuItem/MenuItem";
import { UserBarWithAccounts } from "components/MainSidebar/UserBar/UserBar";
import { useSupportAgentAccess } from "components/SupportAgent/useSupportAgentAccess";
import ThemeSwitcher from "components/ThemeSwitcher/ThemeSwitcher";

import { AQA_LINK_HEADER, AQA_SQLWORKSPACE } from "./AqaIds";
import HelpBar from "./HelpBar/HelpBar";
import { MainSidebarGuest } from "./MainSidebarGuest";
import { useShowMenuItems } from "./useShowMenuItems";

import styles from "./MainSidebar.module.scss";

const CreditIndicator = lazyWithPreload(
  () => import("./CreditIndicator/CreditIndicator")
);

const SupportAccess = lazyWithPreload(
  () => import("./SupportAccess/SupportAccess")
);

export const MainSidebarUser = () => {
  const location = useLocation();
  const accounts = useAccounts();
  const hasOrgAdminRole = useHasAdminRole();
  const { hasAccountAdminRole } = useHasAccountAdminRole();
  const formatPath = useFormatPath();
  const menuItemsVisibility = useShowMenuItems();
  const { t } = useTranslation();
  const enabledOrbac = useEnableOrbac();

  const sqlPath = formatPath(ROUTES.DEVELOP);
  const configurePath = formatPath(ROUTES.CONFIGURE);
  const governPath = formatPath(ROUTES.GOVERN);

  const {
    supportAgentAccessMarkup,
    openSupportAgentAccess,
    openSupportAgentStep,
  } = useSupportAgentAccess();
  const showCreditsIndicator = useCreditsFlag();

  usePreloadComponents([CreditIndicator, SupportAccess]);

  const configureLink = useMemo((): string | null => {
    if (menuItemsVisibility.configure) {
      return configurePath + menuItemsVisibility.configureRoute;
    }

    if (hasOrgAdminRole) {
      return configurePath + "/accounts";
    }

    if (!enabledOrbac && hasAccountAdminRole) {
      // TODO remove /account component when flag is deprecated
      return configurePath + "/account";
    }

    return null;
  }, [
    menuItemsVisibility.configure,
    menuItemsVisibility.configureRoute,
    hasOrgAdminRole,
    hasAccountAdminRole,
    configurePath,
    enabledOrbac,
  ]);

  if (!accounts.length || !accessManager.privileges) {
    return <MainSidebarGuest />;
  }

  return (
    <div className={styles.root}>
      <div className={styles.sidebarHead}>
        <Link
          key="app_header"
          to={sqlPath}
          data-testid={AQA_LINK_HEADER}
          className={styles.logo}
        >
          <LogoIcon />
        </Link>
        <UserBarWithAccounts />

        <div>
          <MenuItem
            className={styles.item}
            testId={AQA_SQLWORKSPACE}
            icon={<SqlIcon />}
            tooltip="Develop"
            disabled={false}
            selected={location.pathname.indexOf(sqlPath) >= 0}
            to={sqlPath}
          />
          {configureLink && (
            <MenuItem
              className={styles.item}
              icon={<ManagementIcon />}
              tooltip={t("title.configure")}
              testId="configure-menu"
              disabled={false}
              selected={location.pathname.indexOf(configurePath) >= 0}
              to={configureLink}
            />
          )}

          {menuItemsVisibility.govern && (
            <MenuItem
              className={styles.item}
              icon={<GovernIcon />}
              tooltip={t("title.govern")}
              testId="govern-menu"
              disabled={false}
              selected={location.pathname.indexOf(governPath) >= 0}
              to={governPath + menuItemsVisibility.governRoute}
            />
          )}
        </div>
      </div>
      <div className={styles.sidebarFooter}>
        {showCreditsIndicator && <CreditIndicator />}
        <SupportAccess openSupportAgentStep={openSupportAgentStep} />
        <HelpBar
          openSupportAgentAccess={openSupportAgentAccess}
          hasAdminRole={hasAccountAdminRole || hasOrgAdminRole}
        />
        <ThemeSwitcher />
        {supportAgentAccessMarkup}
      </div>
    </div>
  );
};
